import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Link } from "react-router-dom";

import Heading from "../../../shared/ui/Typography/Heading";
import Text from "../../../shared/ui/Typography/Text";
import Spinner from "../../../shared/ui/Loading/Spinner";

import { fetchTasksForClient } from "../api/tasks";
import { messageFromError } from "../../../shared/api/drfErrors";

import ClientCreateTaskModal from "./ClientCreateTaskModal";

// ClientTasksSection — subordinate hydration boundary inside
// /clients/:clientId (Phase S8) + T-3 cross-trigger modal owner.
//
// Owns:
//   * its own /tasks/?client=<id> fetch
//   * its own load/error/refresh state
//   * an inline "Оновити" button local to the section header
//   * the "Нова задача" modal lifecycle (open / close / refetch on
//     close). The slice, the modal, and the post-success freshness
//     all stay here.
//
// Does NOT own:
//   * client core (parent owns it)
//   * route validity — section failure does NOT take the route down
//   * any coordination with the contacts/activity sections or the
//     parent
//
// Load lifecycle is duplicated inline from ClientActivitySection /
// ClientContactsSection by design — per CLAUDE.md anti-abstraction
// rules. No useSectionLoader, no shared async hook. Sections stay
// "explicit, local, dumb, disposable".
//
// Narrative posture (S8 §2). This region renders tasks as a
// narrative operational surface — a checklist read from the
// backend, not a launcher widget and not a dashboard panel. The
// per-row checkbox is rendered as a non-interactive visual
// indicator of the `done` state; toggling status is a separate
// per-row mutation that S8 explicitly does not bundle.
//
// T-3 bridge (Phase S8 — architecture-freeze-r8.md §4 T-3 +
// I2 §"R7 §6.4" proposal). Sibling of [[ClientActivitySection]];
// follows the same six structural constraints:
//
//     openTaskModal()           — void, no args, no async, no
//                                 return payload, fire-and-forget.
//
// The parent ([[ClientDetailScreenContent]]) holds a ref to this
// section and may call this method from its header's "+ Нова
// задача" alias button. The parent observes nothing back: it does
// not know whether the modal opened, whether the form was
// submitted, whether a task was saved, or whether the section
// refetched. The parent is presentation-alias only.
//
//   Bridge narrowness (A-1 §6.4 axis-by-axis):
//     (1) one method per ref               — only openTaskModal
//     (2) void return                       — returns undefined
//     (3) no async awaited from caller      — handler does not await
//     (4) no cross-section invocation       — parent calls one ref
//         in a single gesture
//     (5) no registry shape                 — single named method
//     (6) deletable                         — the section ALSO renders
//         its own "+ Нова задача" trigger; the parent's alias is
//         purely additive.

const PRIORITY_LABELS = {
  low: "Низький",
  medium: "Середній",
  high: "Високий",
};

const PRIORITY_ICONS = {
  low: "fa-solid fa-circle",
  medium: "fa-solid fa-circle-minus",
  high: "fa-solid fa-circle-exclamation",
};

const STATUS_LABELS = {
  todo: "До виконання",
  in_progress: "В роботі",
  done: "Виконано",
};


const formatDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const yyyy = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mo}-${dd} ${hh}:${mm}`;
};

const isOverdue = (task) => {
  if (!task || task.status === "done") return false;
  if (!task.due_date) return false;
  const due = new Date(task.due_date);
  if (Number.isNaN(due.getTime())) return false;
  return due.getTime() < Date.now();
};

const unwrapListPayload = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
};

function ClientTasksSection({ clientId }, ref) {
  const [tasks, setTasks] = useState([]);
  const [loadState, setLoadState] = useState("loading");
  const [loadError, setLoadError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(null);

  // Section-owned T-3 modal state (R8 §4 T-3). Local useState only —
  // the parent ClientDetailScreenContent is not informed when this
  // toggles; the parent only knows how to ASK us to open it.
  const [createOpen, setCreateOpen] = useState(false);

  // T-3 narrow bridge. ONE method. void. fire-and-forget.
  // See header docstring for the §6.4 axis-by-axis justification.
  useImperativeHandle(
    ref,
    () => ({
      openTaskModal() {
        setCreateOpen(true);
      },
    }),
    []
  );

  useEffect(() => {
    if (clientId == null) return undefined;

    let cancelled = false;
    setLoadState("loading");
    setLoadError(null);

    fetchTasksForClient(clientId)
      .then((data) => {
        if (cancelled) return;
        setTasks(unwrapListPayload(data));
        setLoadState("loaded");
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(messageFromError(err));
        setLoadState("error");
      });

    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const handleReload = async () => {
    if (refreshing) return;
    setRefreshing(true);
    setRefreshError(null);
    try {
      const data = await fetchTasksForClient(clientId);
      setTasks(unwrapListPayload(data));
      setLoadState("loaded");
    } catch (err) {
      setRefreshError(messageFromError(err));
    } finally {
      setRefreshing(false);
    }
  };

  const list = tasks;

  return (
    <section
      className="cd-section feature-client-detail__section--tasks"
      aria-labelledby="client-detail-tasks-heading"
    >
      <header className="cd-section__head">
        <Heading
          id="client-detail-tasks-heading"
          level={2}
          variant="section"
          icon="fa-solid fa-list-check"
        >
          Задачі
        </Heading>
        <div className="feature-client-detail__section-actions">
          <button
            type="button"
            className="cd-add-btn"
            onClick={() => setCreateOpen(true)}
          >
            + Нова задача
          </button>
          <button
            type="button"
            className="feature-client-detail__refresh-icon-btn"
            onClick={handleReload}
            disabled={refreshing || loadState === "loading"}
            data-loading={refreshing ? "true" : undefined}
            aria-label="Оновити задачі"
            title="Оновити"
          >
            {refreshing ? (
              <Spinner size="sm" label="Оновлення" />
            ) : (
              <i
                className="fa-solid fa-arrows-rotate"
                aria-hidden="true"
              />
            )}
          </button>
        </div>
      </header>

      {refreshError ? (
        <div className="feature-client-detail__refresh-error" role="alert">
          {refreshError}
        </div>
      ) : null}

      {loadState === "loading" ? (
        <div className="feature-client-detail__section-state">
          <Spinner size="md" label="Завантаження задач" />
        </div>
      ) : loadState === "error" ? (
        <div className="feature-client-detail__section-state">
          <Text as="p" size="sm" tone="danger">
            {loadError || "Не вдалося завантажити задачі."}
          </Text>
        </div>
      ) : (
        <>
          <p className="feature-client-detail__tasks-all-link-wrap">
            <Link
              to={`/tasks?client=${clientId}`}
              className="feature-client-detail__tasks-all-link"
            >
              Переглянути всі задачі клієнта
            </Link>
          </p>
          {list.length === 0 ? (
            <Text as="p" size="sm" tone="secondary">
              Активних задач по клієнту немає.
            </Text>
          ) : (
            <ul className="feature-client-detail__list">
              {list.map((task) => {
            const priorityKey = task.priority || "medium";
            const statusKey = task.status || "todo";
            const overdue = isOverdue(task);
            const done = statusKey === "done";
            const taskClassName = [
              "feature-client-detail__list-item",
              "feature-client-detail__list-item--task",
              done ? "feature-client-detail__list-item--task-done" : null,
              overdue
                ? "feature-client-detail__list-item--task-overdue"
                : null,
            ]
              .filter(Boolean)
              .join(" ");
            const priorityLabel =
              PRIORITY_LABELS[priorityKey] || priorityKey || "—";
            const priorityIcon =
              PRIORITY_ICONS[priorityKey] || PRIORITY_ICONS.medium;
            const statusLabel = STATUS_LABELS[statusKey] || statusKey || "—";
            return (
              <li key={task.id} className={taskClassName}>
                <span
                  className="feature-client-detail__task-check"
                  aria-hidden="true"
                  data-state={done ? "done" : "todo"}
                >
                  <i
                    className={
                      done
                        ? "fa-solid fa-square-check"
                        : "fa-regular fa-square"
                    }
                  />
                </span>
                <div className="feature-client-detail__task-body">
                  <strong className="feature-client-detail__task-title">
                    {task.title}
                  </strong>
                  {task.description ? (
                    <p className="feature-client-detail__task-desc">
                      {task.description}
                    </p>
                  ) : null}
                  <div className="feature-client-detail__task-meta">
                    <span
                      className={`feature-client-detail__priority feature-client-detail__priority--${priorityKey}`}
                    >
                      <i className={priorityIcon} aria-hidden="true" />
                      <span>{priorityLabel}</span>
                    </span>
                    {task.due_date ? (
                      <span
                        className={
                          overdue
                            ? "feature-client-detail__task-due feature-client-detail__task-due--overdue"
                            : "feature-client-detail__task-due"
                        }
                      >
                        <i
                          className="fa-solid fa-clock"
                          aria-hidden="true"
                        />
                        <span>
                          {overdue ? "Прострочено: " : "До "}
                          {formatDateTime(task.due_date)}
                        </span>
                      </span>
                    ) : null}
                    <span
                      className={`feature-client-detail__task-status feature-client-detail__task-status--${statusKey}`}
                    >
                      {statusLabel}
                    </span>
                  </div>
                </div>
              </li>
            );
              })}
            </ul>
          )}
        </>
      )}

      <ClientCreateTaskModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        clientId={clientId}
        onSaved={() => {
          setCreateOpen(false);
          handleReload();
        }}
      />
    </section>
  );
}

export default forwardRef(ClientTasksSection);
